/**
 * AdminAuditLogPage — Admin overzicht van alle audit-events (P3).
 *
 * Read-only lijst met filter op actie type + zoeken op user.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Shield, RefreshCw, Search, Loader2, Filter } from 'lucide-react';

const ACTION_COLORS = {
  'login': 'text-emerald-400 bg-emerald-500/10',
  'signup': 'text-cyan-300 bg-cyan-500/10',
  'logout': 'text-white/40 bg-white/5',
  'invoice.create': 'text-blue-300 bg-blue-500/10',
  'website_request.create': 'text-purple-300 bg-purple-500/10',
  'website_request.update': 'text-purple-300 bg-purple-500/10',
  'billing_info.update': 'text-amber-400 bg-amber-500/10',
  'affiliate.approve': 'text-pink-300 bg-pink-500/10',
  'team_user.create': 'text-cyan-300 bg-cyan-500/10',
  'trial.create': 'text-blue-300 bg-blue-500/10',
  'discount_code.create': 'text-[#D4A017] bg-[#D4A017]/10',
};

export default function AdminAuditLogPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/admin/exports/audit-log?limit=500');
      setLogs(r.data.audit_log || []);
    } catch {
      toast.error('Audit-log laden mislukt');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const allActions = Array.from(new Set(logs.map((l) => l.action))).sort();
  const filtered = logs.filter((l) => {
    if (actionFilter && l.action !== actionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (l.actor_email || '').toLowerCase().includes(q) ||
        (l.actor_name || '').toLowerCase().includes(q) ||
        (l.target_id || '').toLowerCase().includes(q) ||
        (l.action || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin · Audit</p>
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              <Shield size={24} className="text-[#D4A017]" /> Audit-log
            </h1>
            <p className="text-white/60 text-sm mt-1">Volledige activiteitenhistorie · laatste 500 events</p>
          </div>
          <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="audit-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op gebruiker, actie, target" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40" data-testid="audit-search" />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-white/40" />
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="audit-filter">
              <option value="">Alle acties</option>
              {allActions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40">Geen audit-events.</div>
        ) : (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Tijdstip</th>
                  <th className="px-4 py-3">Gebruiker</th>
                  <th className="px-4 py-3">Actie</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((l, i) => (
                  <tr key={`${l.timestamp}-${i}`} className="hover:bg-white/5" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-3 text-white/70 text-xs font-mono">{l.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-4 py-3">
                      <p className="text-white text-xs">{l.actor_name || l.actor_email || l.user_id?.slice(0, 8)}</p>
                      <p className="text-white/40 text-[10px]">{l.actor_role || ''}</p>
                    </td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${ACTION_COLORS[l.action] || 'text-white/60 bg-white/5'}`}>{l.action}</span></td>
                    <td className="px-4 py-3 text-white/50 text-xs font-mono">{l.target_id?.slice(0, 14) || '—'}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">
                      {l.meta ? (
                        <details>
                          <summary className="cursor-pointer">tonen</summary>
                          <pre className="whitespace-pre-wrap text-[10px] mt-1">{JSON.stringify(l.meta, null, 2)}</pre>
                        </details>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
